import { ChartInstrument, type ChartInstrumentProps } from 'lib-analytics-ui';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback } from 'react';

import { useAnalyticsState } from '../../../../../../utils/useAnalyticsState/useAnalyticsState';
import { UsePersistInstrumentSettingsResult } from '../../../../utils/usePersistInstrumentSettings';
import { useInstrumentComparison } from '../../../comparison/instrumentComparison.context';

export type ChartInstrumentRendererProps = Pick<
  ChartInstrumentProps,
  'instrument' | 'panel'
> & {
  /** The method to set a specific instrument setting. */
  onSetInstrumentSetting?: UsePersistInstrumentSettingsResult['setInstrumentSetting'];
};

/**
 * A component that abstracts application-specific logic related to rendering a
 * chart instrument, such as tracking callbacks or syncing of chart UI in
 * side-by-side comparison mode.
 */
export const ChartInstrumentRenderer = memo(function ChartInstrumentRenderer({
  panel,
  instrument,
  onSetInstrumentSetting,
}: ChartInstrumentRendererProps): JSX.Element {
  const {
    dashboard,
    settings: { timezone },
  } = useAnalyticsState(panel);

  const comparison = useInstrumentComparison();

  const onChangeGranularity: NonNullable<
    ChartInstrumentProps['onChangeGranularity']
  > = useCallback(
    (granularity): void => {
      onSetInstrumentSetting?.('granularity', granularity);

      ampli.analyticsChartGranularityChanged({
        granularity,
        panel: panel.title,
        dashboard: dashboard.title,
      });
    },
    [dashboard.title, onSetInstrumentSetting, panel.title],
  );

  const onChangeVisualization: NonNullable<
    ChartInstrumentProps['onChangeVisualization']
  > = useCallback(
    (visualization): void => {
      onSetInstrumentSetting?.('visualization', visualization);

      ampli.analyticsChartVisualizationChanged({
        visualization,
        panel: panel.title,
        dashboard: dashboard.title,
      });
    },
    [dashboard.title, onSetInstrumentSetting, panel.title],
  );

  const onChangeFormat: NonNullable<ChartInstrumentProps['onChangeFormat']> =
    useCallback(
      (format): void => {
        onSetInstrumentSetting?.('format', format);

        ampli.analyticsChartFormatChanged({
          format,
          panel: panel.title,
          dashboard: dashboard.title,
        });
      },
      [dashboard.title, onSetInstrumentSetting, panel.title],
    );

  return (
    <ChartInstrument
      panel={panel}
      instrument={instrument}
      timezone={timezone}
      syncOptions={comparison?.syncOptions.chart}
      onChangeGranularity={onChangeGranularity}
      onChangeVisualization={onChangeVisualization}
      onChangeFormat={onChangeFormat}
    />
  );
});
